import type { DeckEntry, SideboardEntry } from '@mtg-deck-tool/shared';

/**
 * Formats a deck for Moxfield-compatible plain-text file import.
 *
 * Format: main deck cards first (commanders then non-commanders, preserving
 * store order within each group), then one blank line, then sideboard cards.
 * No section headers. Blank line omitted if no sideboard cards.
 *
 * Each line: `{quantity} {name} ({SET_UPPERCASE}) {collector_number}`
 * If collectorNumber is an empty string, the trailing ` {collector_number}`
 * is omitted.
 *
 * @param entries - All deck entries (commanders + non-commanders combined).
 * @param sideboard - All sideboard entries.
 * @returns The formatted plain-text file content as a string.
 */
export function formatDeckAsFile(entries: DeckEntry[], sideboard: SideboardEntry[]): string {
  const commanders = entries.filter((e) => e.isCommander);
  const rest = entries.filter((e) => !e.isCommander);
  const mainEntries = [...commanders, ...rest];

  const formatEntry = (name: string, quantity: number, set: string, collectorNumber: string): string => {
    const setUpper = set.toUpperCase();
    if (collectorNumber) {
      return `${quantity} ${name} (${setUpper}) ${collectorNumber}`;
    }
    return `${quantity} ${name} (${setUpper})`;
  };

  const mainLines = mainEntries.map((e) =>
    formatEntry(e.card.name, e.quantity, e.card.set, e.card.collectorNumber),
  );

  const sideboardLines = sideboard.map((e) =>
    formatEntry(e.card.name, 1, e.card.set, e.card.collectorNumber),
  );

  if (sideboardLines.length > 0) {
    return mainLines.join('\n') + '\n\n' + sideboardLines.join('\n');
  }

  return mainLines.join('\n');
}

/**
 * Sanitises a deck name for use as a filename.
 * Spaces become dashes, special characters are stripped.
 * Falls back to 'deck' if the result is empty.
 *
 * @param deckName - The raw deck name from the store.
 * @returns A filesystem-safe filename base (without extension).
 */
export function sanitiseDeckName(deckName: string): string {
  return deckName
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9\-_]/g, '')
    || 'deck';
}
